import { createContext, useContext } from 'react';
import type { AccountInfo, MessageContext, Queries, Session } from '@sl/core';

// What the global shell has already loaded by the time a screen under it renders (ARCH §9.1, §9.3): the
// session, the account the shell is showing, the query factories bound to that account's freshness, and the
// message context bound to its currency and time zone. A screen reads them instead of re-fetching:
//
//   const { account, queries } = useShell();
//   const summary = useQuery(queries.summary(account.id, periodQueryOf(view, account.today)));
//
// The shell is the layout route of every authenticated screen, so this context is always present below it;
// `useShell()` outside it is a programming error, not a state to handle.

export interface ShellContextValue {
  session: Session;
  /** The selected session account; validated against the server list on every bootstrap. */
  account: AccountInfo;
  /** `createQueries(client, { freshness: freshnessOf(account) })` — the ONE set for this account. */
  queries: Queries;
  /** `render(message, messages)` for every sentence that carries data (ARCH §2.7). */
  messages: MessageContext;
  /**
   * The app's locale, read once next to the Clock (T-015's rule). A screen needs it to build its own
   * `createFormatter({ account, locale, deviceTimeZone })` — the message context above is already bound to
   * it, but a figure that is not a sentence (a KPI value, a chart's axis) is formatted directly.
   */
  locale: string;
  /** IANA zone of the device (REQ-261 AC4) — what "12 min ago" is counted in. */
  deviceTimeZone: string;
}

export const ShellContext = createContext<ShellContextValue | null>(null);

export function useShell(): ShellContextValue {
  const value = useContext(ShellContext);
  if (!value) throw new Error('useShell() needs the <AppShell> layout route above it.');
  return value;
}

/** The account the shell is showing. */
export function useShellAccount(): AccountInfo {
  return useShell().account;
}

/** The query factories bound to this account (`@sl/core/queries`). */
export function useQueries(): Queries {
  return useShell().queries;
}

/** The message context bound to this account (`render(message, ctx)`). */
export function useMessages(): MessageContext {
  return useShell().messages;
}
